import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Stack,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

/**
 * Consulta rápida de precios.
 * Usa GET /catalogos/consulta-rapida (una sola query en backend).
 * Solo productos habilitados y no descontinuados.
 * Clic en descripción → copia el nombre.
 *
 * Página:  <ConsultaPrecios standalone />
 * Modal:   <ConsultaPrecios open={open} onClose={...} />
 */
const ConsultaPrecios = ({ open = true, onClose, standalone = false }) => {
  const [items, setItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const formatMoney = (value) => {
    if (value == null || isNaN(value)) return '—';
    return Number(value).toLocaleString('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchItems = useCallback(async (q = '') => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      if (q && q.trim()) params.q = q.trim();

      const res = await api.get('/catalogos/consulta-rapida', {
        params,
        signal: controller.signal,
      });

      if (!isMounted.current) return;
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      if (isMounted.current) {
        setSnackbar({ open: true, message: 'Error al cargar productos', severity: 'error' });
        setItems([]);
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchItems('');
  }, [fetchItems]);

  // Debounce búsqueda → backend
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchItems(value);
    }, 300);
  };

  const handleCopy = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = texto;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setSnackbar({ open: true, message: `Copiado: "${texto}"`, severity: 'success' });
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por descripción o referencia..."
          value={searchInput}
          onChange={handleSearchChange}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {items.length} producto{items.length !== 1 ? 's' : ''}
          {searchInput ? ' (filtrados)' : ''} · Clic en la descripción para copiar
        </Typography>
      </Box>

      {loading && items.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            {searchInput ? 'No se encontraron productos' : 'No hay productos habilitados'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Marca</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', minWidth: 180 }}>
                  Precio(s)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => {
                const precios = Array.isArray(row.precios) ? row.precios : [];

                return (
                  <TableRow key={row.id_catalogo} hover>
                    {/* Descripción - clic para copiar */}
                    <TableCell
                      onClick={() => handleCopy(row.descripcion)}
                      sx={{ py: 1.2, cursor: 'pointer', verticalAlign: 'top' }}
                    >
                      <Tooltip title="Clic para copiar">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" fontWeight={500} sx={{ maxWidth: 320 }}>
                            {row.descripcion}
                          </Typography>
                          <CopyIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                        </Stack>
                      </Tooltip>
                      {row.referencia && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ref: {row.referencia}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Marca */}
                    <TableCell sx={{ py: 1.2, verticalAlign: 'top' }}>
                      <Typography variant="body2" noWrap>
                        {row.nombre_marca || '—'}
                      </Typography>
                    </TableCell>

                    {/* Precios visibles de un vistazo con desglose de IVA */}
                    <TableCell sx={{ py: 1, verticalAlign: 'top' }}>
                      {precios.length === 0 ? (
                        <Typography variant="body2" color="warning.main">
                          Sin precio
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {precios.map((p, idx) => (
                            <Box
                              key={p.id || idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              {/* Bloque con Precio con IVA y sin IVA */}
                              <Box display="flex" flexDirection="column" alignItems="flex-start">
                                <Typography
                                  variant="body2"
                                  fontWeight={idx === 0 ? 700 : 500}
                                  color={idx === 0 ? 'success.dark' : 'text.primary'}
                                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                  $ {(parseFloat(p.precio) || 0).toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  (Sin IVA: ${((parseFloat(p.precio) || 0) / 1.13).toFixed(4)})
                                </Typography>
                              </Box>

                              {/* Nombre de la lista de precios si aplica */}
                              {p.nombre_lista && p.nombre_lista !== 'Sin lista' && (
                                <Chip
                                  label={`${p.nombre_lista}${p.es_lista_default ? ' ★' : ''}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', mt: 0.2 }}
                                />
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const snackbarEl = (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={1800}
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={snackbar.severity}
        variant="filled"
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  if (standalone) {
    return (
      <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
  {/* Contenedor Flex con distribución espacio-between */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
    
    {/* Grupo Izquierdo: Volver + Título */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/clientes')}
      >
        Volver
      </Button>
      
      <Typography variant="h6" component="h1">
        Consulta de Precios
      </Typography>
    </Box>

    {/* Grupo Derecho: Botón Cerrar */}
    <IconButton onClick={() => navigate('/clientes')} sx={{ color: 'text.secondary' }}>
      <CloseIcon />
    </IconButton>
    
  </Box>

  {content}
  {snackbarEl}
</Box>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: 700 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6">Consulta rápida de precios</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {content}
        </DialogContent>
      </Dialog>
      {snackbarEl}
    </>
  );
};

export default ConsultaPrecios;